import { useState } from 'react';
import { domPurifyWithConfigs } from '@wonderful/wwc/dist/src/utils/domPurifyWithConfigs';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';

import { Accordion } from '@wonderful/wwc/dist/src/common/Accordion/Accordion';
import { AccordionElement } from '@wonderful/wwc/dist/src/common/Accordion/AccordionElement';
import { AccordionButton } from '@wonderful/wwc/dist/src/common/Accordion/AccordionButton';
import { AccordionPanel } from '@wonderful/wwc/dist/src/common/Accordion/AccordionPanel';

import { stripHtml } from '@/helpers/utils';
import { useTranslation } from '@/app/i18n/TranslationContext';
import { FilterableAccordionProps, FAQItem } from './FilterableAccordion.types';

import { BuilderEditingHelper } from '@wonderful/wwc/dist/src/common/BuilderEditingHelper/BuilderEditingHelper';
import Section from '@/components/design/Section/Section';
import Filter from '@/components/modules/Filter/Filter';
import styles from './FilterableAccordion.module.scss';

export default function FilterableAccordion(props: FilterableAccordionProps) {
  const { isFilterEnabled, questions, faqFilterTags, builderBlock } = props;
  const componentId = builderBlock?.id;

  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState(`${t('all')}`.toLowerCase());

  const [isInViewport, setIsInViewport] = useState(false);

  const onEnterViewport = () => {
    setIsInViewport(true);
  };

  // Render builder editing helper block if questions or filter tags are empty
  if (!questions && !faqFilterTags) {
    return (
      <BuilderEditingHelper
        componentName="FilterableAccordion"
        visible={true}
      />
    );
  }

  const faqSchemaObject = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions?.map(i => ({
      '@type': 'Question',
      name: stripHtml(i.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(i.answer),
      },
    })),
  };

  const faqTags: Set<string> = new Set();

  // Go through each question item and add only unique tags to array of filterTags
  faqFilterTags?.forEach(({ tag }) => {
    if (tag) {
      faqTags.add(tag.toLowerCase());
    }
  });

  const filterTags = Array.from(faqTags);

  return (
    <>
      <Section
        className={clsx(
          'container',
          styles.accordionSection,
          isInViewport ? styles.inViewport : ''
        )}
        onEnterViewport={onEnterViewport}
        data-testid="filterable-accordion"
      >
        <div className="row">
          <div className="col-12">
            {isFilterEnabled && (
              <Filter
                tags={filterTags}
                currentFilter={activeFilter}
                componentId={componentId}
                ariaLabel="FAQ Filters"
                setActiveFilter={setActiveFilter}
                data-testid={'faq-filter'}
              />
            )}
            <Accordion
              multiple
              collapsible
              className={styles.accordionList}
              role="list"
              id={componentId}
            >
              {questions?.map((item, index) => (
                <FilteredAccordionItems
                  id={`${componentId}-${index}`}
                  key={item.question}
                  item={item}
                  activeFilter={activeFilter}
                  index={index}
                />
              ))}
            </Accordion>
          </div>
        </div>
      </Section>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: domPurifyWithConfigs(JSON.stringify(faqSchemaObject)),
        }}
      />
    </>
  );
}

function FilteredAccordionItems(props: {
  id: string;
  item: FAQItem;
  activeFilter: string;
  index: number;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { id, item, activeFilter, index } = props;

  const tags: string[] = [];

  // Loop through tags and reformat each tag in the array as a string instead of object
  item?.tags?.forEach(({ tag }) => {
    if (tag) {
      tags.push(tag.toLowerCase());
    }
  });

  const isHidden =
    !tags?.includes(activeFilter) &&
    activeFilter !== `${t('all')}`.toLowerCase();

  return (
    <AccordionElement
      isOpen={isOpen}
      id={id + '-item'}
      key={item.question}
      className={clsx(
        styles.accordionListItem,
        isHidden && styles.accordionListItemHidden
      )}
      aria-hidden={isHidden}
    >
      <AccordionButton
        index={index}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: domPurifyWithConfigs(item.question),
          }}
        />
        <span className={styles.plusMinus}></span>
      </AccordionButton>

      <AccordionPanel index={index} isOpen={isOpen}>
        <div
          tabIndex={isOpen ? 0 : -1}
          dangerouslySetInnerHTML={{
            __html: domPurifyWithConfigs(item.answer),
          }}
        />
      </AccordionPanel>
    </AccordionElement>
  );
}
